import {CommonModule} from '@angular/common';
import {Component, OnInit} from '@angular/core';
import {
    SearchFilterComponent,
    FilterConfig
} from '@/components/search-filter/search-filter.component';
import {FormBuilder, FormGroup, ReactiveFormsModule} from '@angular/forms';
import {RouterModule} from '@angular/router';
import {VaccineService} from '@services/vaccine.service';
import {ToastrService} from 'ngx-toastr';
import {SupplierService} from '@services/supplier.service';
import {UserService} from '@services/user.service';
import {MedicationService} from '@services/medication.service';
import {DialogModule} from 'primeng/dialog';
import {OverlayPanel, OverlayPanelModule} from 'primeng/overlaypanel';
import {PaginatorModule} from 'primeng/paginator';
import {TableModule} from 'primeng/table';
import {TooltipModule} from 'primeng/tooltip';
import {splitByUpperCase} from '@/utils/helpers';
import {HasRoleDirective} from '@/has-role.directive';
@Component({
    selector: 'app-vaccine-list',
    standalone: true,
    imports: [
        CommonModule,
        RouterModule,
        TableModule,
        PaginatorModule,
        DialogModule,
        OverlayPanelModule,
        TooltipModule,
        ReactiveFormsModule,
        SearchFilterComponent,
        HasRoleDirective
    ],
    templateUrl: './vaccine-list.component.html',
    styleUrl: './vaccine-list.component.scss'
})
export class VaccineListComponent implements OnInit {
    currentPage: number = 1;
    pageSize: number = 10;
    totalRecords: number = 0;
    public loading = true;
    public data: any[] = [];
    public selectedRecordId: number = 0;
    public displayDeleteDialog = false;
    public name: string = '';
    public filterForm: FormGroup;
    public filteringData: any = null;
    public isPaginator: boolean = true;
    public splitByUpperCase = splitByUpperCase;
    filterConfig: FilterConfig;
    currentUserId: number = 0;
    supplierOptions: {value: any; label: string}[] = [];
    classificationOptions: {value: any; label: string}[] = [];
    userOptions: {value: any; label: string}[] = [];
    today = new Date();

    constructor(
        private vaccineService: VaccineService,
        private toastr: ToastrService,
        private fb: FormBuilder,
        private supplierService: SupplierService,
        private userService: UserService,
        private classificationService: MedicationService
    ) {}

    ngOnInit() {
        // Fetch options for supplier, classification, user
        this.supplierService.getSupplierList().subscribe((res: any[]) => {
            this.supplierOptions = (res || []).map((s) => ({
                value: s.id,
                label: s.name
            }));
            this.updateFilterConfig();
        });
        this.classificationService
            .getClassificationList()
            .subscribe((res: any[]) => {
                this.classificationOptions = (res || []).map((c) => ({
                    value: c.id,
                    label: c.name
                }));
                this.updateFilterConfig();
            });
        this.userService
            .getUserList('', '', '', 1, 100)
            .subscribe((res: any) => {
                this.userOptions = (res?.users || []).map((u) => ({
                    value: u.id,
                    label: u.name || u.username || u.email
                }));
                this.updateFilterConfig();
            });

        this.updateFilterConfig();
        this.filterForm = this.fb.group({
            BrandName: [''],
            BatchNumber: ['']
        });
        this.getVaccineList();
    }

    updateFilterConfig() {
        this.filterConfig = {
            fields: [
                {
                    key: 'BatchNumber',
                    label: 'Batch Number',
                    type: 'text',
                    placeholder: 'Batch Number'
                },
                {
                    key: 'BrandName',
                    label: 'Brand Name',
                    type: 'text',
                    placeholder: 'Brand Name'
                },
                {
                    key: 'ClassificationId',
                    label: 'Classification',
                    type: 'select',
                    options: this.classificationOptions,
                    placeholder: 'Select Classification'
                },
                {
                    key: 'SupplierId',
                    label: 'Supplier',
                    type: 'select',
                    options: this.supplierOptions,
                    placeholder: 'Select Supplier'
                }
            ]
        };
    }

    onCustomFilter(filter: any) {
        this.filteringData = filter;
        this.currentPage = 1;
        this.getVaccineList(this.filteringData);
    }

    onCustomFilterClear() {
        this.filteringData = null;
        this.currentPage = 1;
        this.getVaccineList();
    }

    getVaccineList(filters?: any) {
        this.loading = true;
        this.vaccineService
            .getVaccineList(this.currentPage, this.pageSize, filters)
            .subscribe((response) => {
                if (response.totalRecords > 0) {
                    this.totalRecords = response.totalRecords;
                    this.data = response.vaccine;
                    this.isPaginator = true;
                    this.loading = false;
                } else {
                    this.isPaginator = false;
                    this.data = response.vaccine;
                    this.toastr.warning('No Records Found');
                    this.loading = false;
                }
            });
    }

    getVaccineTypeName(id: number): string {
        const classification = this.classificationOptions.find(
            (c) => c.value === id
        );
        return classification?.label ?? '-';
    }

    deleteVaccine(vaccineId: number) {
        this.selectedRecordId = vaccineId;
        this.displayDeleteDialog = true;
    }
    deleteItem() {
        if (this.selectedRecordId) {
            this.vaccineService
                .deleteVaccine(this.selectedRecordId)
                .subscribe((response) => {
                    this.displayDeleteDialog = false;
                    this.selectedRecordId = 0;
                    this.toastr.success('Vaccine deleted successfully.');
                    this.getVaccineList();
                });
        }
    }

    onPageChange(event: any) {
        this.currentPage = event.page + 1;
        this.getVaccineList(this.filteringData);
    }

    filterVaccine(): void {
        this.getVaccineList(this.name);
    }

    filterPlans(event: Event, overlayPanel: OverlayPanel): void {
        overlayPanel.toggle(event);
    }

    clearFilter() {
        this.filterForm.reset();
        this.getVaccineList();
    }

    onSearch(overlayPanel: OverlayPanel) {
        const filterValues = this.filterForm.value;
        this.filteringData = {
            BrandName: filterValues.BrandName?.trim() || null,
            BatchNumber: filterValues.BatchNumber?.trim() || null
        };
        this.currentPage = 1;
        overlayPanel.hide();
        this.getVaccineList(this.filteringData);
    }
}
